// src/hooks/useReintentoImagenes.js
//
// Estado de carga de una o varias imágenes, por dirección. Antes de dar una imagen por perdida la reintenta y
// comprueba si el archivo existe (el porqué y los tiempos, en src/utils/imagenReintento.js).
//
// Estados: 'cargando' (se muestra la <img>) · 'esperando' (entre reintentos o comprobando) · 'vencida' (el archivo ya
// no existe) · 'fallo' (no cargó, pero no está borrado o no se pudo saber: se ofrece reintentar).
//
// Uso:
//   const { srcDe, estadoDe, alError, reintentar } = useReintentoImagenes();
//   estadoDe(url) === 'cargando' && <img key={srcDe(url)} src={srcDe(url)} onError={() => alError(url)} />
// 🔴 La <img> va con `key={srcDe(url)}`: cada intento es un elemento nuevo y un aviso viejo no se cruza con el nuevo.
// Si la misma dirección se muestra en dos <img> a la vez (imagen grande + miniatura), su fallo se cuenta una vez.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ESPERAS_REINTENTO_MS, urlConReintento, comprobarImagen } from '../utils/imagenReintento';

const INICIAL = { n: 0, fallos: 0, estado: 'cargando' };

export default function useReintentoImagenes() {
  const [porUrl, setPorUrl] = useState({}); // url → { n: intento de la dirección, fallos: reintentos automáticos, estado }
  const porUrlRef = useRef(porUrl);
  const esperas = useRef(new Set());
  const vivo = useRef(true);

  useEffect(() => {
    vivo.current = true;
    const pendientes = esperas.current;
    return () => {
      vivo.current = false;
      pendientes.forEach(clearTimeout);
      pendientes.clear();
    };
  }, []);

  // La referencia se actualiza en el acto: dos avisos seguidos de la misma imagen ven el estado nuevo
  const poner = useCallback((url, cambios) => {
    if (!vivo.current) return;
    const nuevo = { ...porUrlRef.current, [url]: { ...(porUrlRef.current[url] || INICIAL), ...cambios } };
    porUrlRef.current = nuevo;
    setPorUrl(nuevo);
  }, []);

  const srcDe = useCallback((url) => urlConReintento(url, porUrl[url]?.n || 0), [porUrl]);
  const estadoDe = useCallback((url) => porUrl[url]?.estado || 'cargando', [porUrl]);

  const alError = useCallback((url) => {
    const s = porUrlRef.current[url] || INICIAL;
    if (s.estado !== 'cargando') return; // este fallo ya se está atendiendo
    poner(url, { estado: 'esperando' });
    if (s.fallos < ESPERAS_REINTENTO_MS.length) {
      const t = setTimeout(() => {
        esperas.current.delete(t);
        poner(url, { estado: 'cargando', n: s.n + 1, fallos: s.fallos + 1 });
      }, ESPERAS_REINTENTO_MS[s.fallos]);
      esperas.current.add(t);
      return;
    }
    comprobarImagen(url).then((resultado) => {
      if (resultado === 'fallo') console.warn('[imagen] No cargó después de 3 intentos y el archivo no está borrado:', url);
      poner(url, { estado: resultado });
    });
  }, [poner]);

  // Botón "Reintentar": dirección nueva y otra vez los reintentos automáticos
  const reintentar = useCallback((url) => {
    const s = porUrlRef.current[url] || INICIAL;
    poner(url, { estado: 'cargando', n: s.n + 1, fallos: 0 });
  }, [poner]);

  return { srcDe, estadoDe, alError, reintentar };
}
